import { Fragment } from 'react';
import { type useChat } from '@ai-sdk/react';
import { isToolUIPart } from 'ai';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { AIAssistantToolNames, type AIAssistantToolName } from 'balus-common';
import { useTranslation } from 'react-i18next';
import { ToolPartToggle } from './ToolPartToggle';
import { ToolExecutingIndicator } from './ToolExecutingIndicator';
import { ChatImageThumbnail } from './ChatImageThumbnail';
import { ChatFileChip } from './ChatFileChip';
import { ExportFileCard } from './ExportFileCard';
import { ViewImageCard } from './ViewImageCard';
import { SearchViewModelHitLinks } from './SearchViewModelHitLinks';

type Message = ReturnType<typeof useChat>['messages'][number];

// 本文と同じ見た目だとリンクだと気づけないため、色と hover 時の下線を持たせる
// （prose のリンク色はベースの prose クラスに紐づくので、prose-sm / prose-slate だけでは効かない）
const CHAT_LINK_CLASS = 'text-blue-600 underline-offset-2 hover:underline';

// CJK記号・ひらがな・カタカナ・漢字・全角英数記号
const CJK_TEXT_PATTERN = /[\u3000-\u303f\u3040-\u30ff\u4e00-\u9fff\uff00-\uffef]/;

/**
 * GFM の自動リンクは空白か `<` までを1つのURLとみなすため、
 * 「https://example.com/aはどうでしょうか？」のようにURL直後へ日本語が続くと日本語までリンクに含まれ、
 * クリックすると壊れたURLへ飛んでしまう。日本語が始まる位置でURLと本文に切り分ける。
 */
function splitAutolinkTail(href: string, label: string): { href: string; label: string; tail: string } | null {
  const cut = label.search(CJK_TEXT_PATTERN);
  if (cut <= 0) {
    return null;
  }
  const tail = label.slice(cut);
  // href 側は micromark がパーセントエンコード済みのことがあるので両方の形で末尾を照合する
  const encodedTail = encodeURI(tail);
  const suffix = href.endsWith(tail) ? tail : href.endsWith(encodedTail) ? encodedTail : undefined;
  if (suffix === undefined) {
    return null;
  }
  const trimmedHref = href.slice(0, href.length - suffix.length);
  // ドメイン自体が日本語のURLは切り分けられないため、そのまま1つのリンクとして残す
  if (trimmedHref.replace(/^(https?:\/\/|mailto:)/i, '') === '') {
    return null;
  }
  return { href: trimmedHref, label: label.slice(0, cut), tail };
}

const markdownComponents: Components = {
  a: ({ node, children, href, ...props }) => {
    const split = typeof children === 'string' && href ? splitAutolinkTail(href, children) : null;
    if (split) {
      return (
        <>
          <a {...props} href={split.href} target="_blank" rel="noreferrer" className={CHAT_LINK_CLASS}>
            {split.label}
          </a>
          {split.tail}
        </>
      );
    }
    return (
      <a {...props} href={href} target="_blank" rel="noreferrer" className={CHAT_LINK_CLASS}>
        {children}
      </a>
    );
  },
  // チャット本文には取得したWebページ由来の不信頼テキストが混ざりうる。
  // Markdown の画像記法はクリックなしで外部リクエストを発生させ、データ流出経路になるためレンダリングしない。
  img: ({ alt }) => (alt ? <span className="text-gray-500">[{alt}]</span> : null),
};

// 同じ理由で、リンク先も http/https/mailto に限定する
function transformMarkdownUrl(url: string): string {
  return /^(https?|mailto):/i.test(url) ? url : '';
}

function fetchUrlHostOf(input: unknown): string | undefined {
  const url = (input as { url?: unknown } | null | undefined)?.url;
  if (typeof url !== 'string') return undefined;
  try {
    return new URL(url).hostname;
  } catch {
    return undefined;
  }
}

// 履歴のファイルチップから配信プロキシ経由で原本をダウンロードする。
// 応答は Content-Disposition: attachment のため、ページ遷移せずダウンロードが発火する。
function downloadChatFile(url: string, filename?: string): void {
  const anchor = document.createElement('a');
  anchor.href = url;
  if (filename) anchor.download = filename;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

export function AIChatMessageContent({ message }: { message: Message }) {
  const { t } = useTranslation();
  return (
    <>
      {message.parts.map((part, index) => {
        switch (true) {
          case part.type === 'text': {
            return (
              <ReactMarkdown
                key={`${message.id}-text-${index}`}
                remarkPlugins={[remarkGfm]}
                className="prose-sm prose-slate"
                components={markdownComponents}
                urlTransform={transformMarkdownUrl}
              >
                {part.text}
              </ReactMarkdown>
            );
          }

          case part.type === 'step-start':
            return null;

          case isToolUIPart(part): {
            const toolLabelDict = {
              [AIAssistantToolNames.getViews]: t('viewModel.aiChatTool.getViews'),
              [AIAssistantToolNames.getViewContent]: t('viewModel.aiChatTool.getViewContent'),
              [AIAssistantToolNames.getViewImage]: t('viewModel.aiChatTool.getViewImage'),
              [AIAssistantToolNames.addView]: t('viewModel.aiChatTool.addView'),
              [AIAssistantToolNames.getCurrentViews]: t('viewModel.aiChatTool.getCurrentViews'),
              [AIAssistantToolNames.getClientSkill]: t('viewModel.aiChatTool.getClientSkill'),
              [AIAssistantToolNames.generateIds]: t('viewModel.aiChatTool.generateIds'),
              [AIAssistantToolNames.skillList]: t('viewModel.aiChatTool.skillList'),
              [AIAssistantToolNames.skill]: t('viewModel.aiChatTool.skill'),
              [AIAssistantToolNames.patchView]: t('viewModel.aiChatTool.patchView'),
              [AIAssistantToolNames.listClientSkills]: t('viewModel.aiChatTool.listClientSkills'),
              [AIAssistantToolNames.switchMode]: t('viewModel.aiChatTool.switchMode'),
              [AIAssistantToolNames.createClientSkill]: t('viewModel.aiChatTool.createClientSkill'),
              [AIAssistantToolNames.updateClientSkill]: t('viewModel.aiChatTool.updateClientSkill'),
              [AIAssistantToolNames.readAttachedSpreadsheet]: t('viewModel.aiChatTool.readAttachedSpreadsheet'),
              [AIAssistantToolNames.readAttachedTextFile]: t('viewModel.aiChatTool.readAttachedTextFile'),
              [AIAssistantToolNames.exportFile]: t('viewModel.aiChatTool.exportFile'),
              [AIAssistantToolNames.fetchUrl]: t('viewModel.aiChatTool.fetchUrl'),
              [AIAssistantToolNames.searchViewModels]: t('viewModel.aiChatTool.searchViewModels'),
              [AIAssistantToolNames.getOtherViewModelViews]: t('viewModel.aiChatTool.getOtherViewModelViews'),
              [AIAssistantToolNames.getOtherViewModelViewContent]: t(
                'viewModel.aiChatTool.getOtherViewModelViewContent'
              ),
            } satisfies Record<AIAssistantToolName, string>;
            const toolNameKey = part.type.substring('tool-'.length);
            const fetchUrlHost = toolNameKey === AIAssistantToolNames.fetchUrl ? fetchUrlHostOf(part.input) : undefined;
            // 取得先は折りたたみを開かないと見えないため、ラベルにホスト名を出す
            const toolName = fetchUrlHost
              ? t('viewModel.aiChatTool.fetchUrlWithHost', { host: fetchUrlHost })
              : (toolLabelDict[toolNameKey as AIAssistantToolName] ?? toolNameKey);

            // ツール実行中（input-streaming または input-available）の場合はインジケーターを表示
            if (part.state === 'input-streaming' || part.state === 'input-available') {
              return (
                <ToolExecutingIndicator
                  key={`${message.id}-tool-${index}-${part.toolCallId}`}
                  toolName={toolName}
                  state={part.state}
                />
              );
            }

            // 完了またはエラーの場合は従来の折りたたみ表示
            const hasError = part.errorText;

            // getViewImage の成功時は取得した画像を表示する（エラー時は下の折りたたみでエラー内容を出す）。
            // 折りたたみの JSON へ回すと、base64 の画像を毎レンダー文字列化することにもなる。
            if (toolNameKey === AIAssistantToolNames.getViewImage && !hasError) {
              return <ViewImageCard key={`${message.id}-tool-${index}-${part.toolCallId}`} output={part.output} />;
            }

            // exportFile の成功時はダウンロードカードを表示する（エラー時は下の折りたたみでエラー内容を出す）。
            if (toolNameKey === AIAssistantToolNames.exportFile && !hasError) {
              return (
                <ExportFileCard
                  key={`${message.id}-tool-${index}-${part.toolCallId}`}
                  toolCallId={part.toolCallId}
                  input={part.input}
                />
              );
            }

            const label = hasError ? t('viewModel.aiChatTool.errorSuffix', { toolName }) : toolName;
            const toolPartToggle = (
              <ToolPartToggle label={label}>
                <>
                  toolCallId: {JSON.stringify(part.toolCallId, null, '  ')}
                  <br />
                  {part.output && (
                    <>
                      output: {JSON.stringify(part.output, null, '  ')}
                      <br />
                    </>
                  )}
                  {part.errorText && (
                    <>
                      output: {JSON.stringify(part.errorText, null, '  ')}
                      <br />
                    </>
                  )}
                  input: {JSON.stringify(part.input, null, '  ')}
                </>
              </ToolPartToggle>
            );
            return (
              <Fragment key={`${message.id}-tool-${index}-${part.toolCallId}`}>
                {toolPartToggle}
                {toolNameKey === AIAssistantToolNames.searchViewModels && !hasError && (
                  <SearchViewModelHitLinks output={part.output} />
                )}
              </Fragment>
            );
          }

          case part.type === 'reasoning': {
            // 中身が空の reasoning は表示しない（streaming 中の空や、要約が無いケース）
            if (!part.text.trim()) {
              return null;
            }
            // tool 呼び出しと同様に折りたたみで表示する
            return (
              <ToolPartToggle key={`${message.id}-reasoning-${index}`} label={t('viewModel.aiChat.reasoningLabel')}>
                {part.text}
              </ToolPartToggle>
            );
          }
          // 添付画像はサムネイル表示（クリックでライトボックス）。JSON ダンプの case 群より前に置く。
          case part.type === 'file' && part.mediaType.startsWith('image/'): {
            return <ChatImageThumbnail key={`${message.id}-file-${index}`} src={part.url} alt={part.filename} />;
          }

          // 添付ファイル（スプレッドシート・テキストファイル）はチップ表示（クリックでプロキシ経由ダウンロード）。
          case part.type === 'file' &&
            (part.url.startsWith('/ai-chat-files/') || part.url.startsWith('/ai-chat-text-files/')): {
            return (
              <ChatFileChip
                key={`${message.id}-file-${index}`}
                filename={part.filename ?? t('viewModel.aiChat.attachmentFallbackName')}
                mediaType={part.mediaType}
                onClick={() => downloadChatFile(part.url, part.filename)}
              />
            );
          }

          case part.type === 'source-url':
          case part.type.startsWith('data-'):
          case part.type === 'file':
          case part.type === 'dynamic-tool':
          case part.type === 'source-document':
            return (
              <div key={`${message.id}-text-${index}`} className="my-1 whitespace-pre-wrap text-xs text-gray-500">
                {JSON.stringify(part)}
              </div>
            );

          default:
            return (
              <div key={`${message.id}-text-${index}`} className="my-1 whitespace-pre-wrap text-xs text-gray-500">
                {JSON.stringify(part)}
              </div>
            );
            console.warn('Unknown message part type:', part);
        }
      })}
    </>
  );
}
